// hooks/useSchemaView.js - REFACTORED VERSION
import { useEffect, useState, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import {
  useStation,
  useImpersonatedUserId,
  useLoggedInViaLink
} from '../contexts/GlobalStateContext';

// Import sub-hooks
import { useEditLocks } from './signalr/useEditLocks';
import { useTypingIndicators } from './signalr/useTypingIndicators';
import { useSignalRHeartbeat } from './signalr/useSignalRHeartbeat';

// Import utilities
import { createConnection, createHubUrl } from './signalr/signalRConfig';
import { createEventHandlers, registerEventHandlers } from './signalr/signalREventHandlers';
import { getTodaysDate } from '../services/UtilityServices';

export const useSchemaView = () => {
  // ========== REFS ==========
  const connectionRef = useRef(null);
  const documentSavedCallbackRef = useRef(null);
  const isPublicViewerRegisteredRef = useRef(false);  // ✅ Track om redan registrerad
  const isPublicViewerFromTokenRef = useRef(false);
  const hasHandledInitialStationRef = useRef(false);
  const connectionReadyResolversRef = useRef([]);

  // ========== BASIC STATE ==========
  const [scheduleMetadata, setScheduleMetadata] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  // ========== CONTEXT ==========
  const { station } = useStation();
  const { impersonatedUserId } = useImpersonatedUserId();
  const { loggedInViaLink } = useLoggedInViaLink();

  // Hålls uppdaterade via egna effekter nedan så att SignalR-callbacks (som kan triggas
  // långt efter att de skapades, t.ex. onreconnected eller event handlers) alltid läser
  // aktuellt station/schema-state istället för att fånga ett gammalt värde i sin closure.
  const stationRef = useRef(station);
  const scheduleMetadataRef = useRef(scheduleMetadata);

  useEffect(() => {
    stationRef.current = station;
  }, [station]);

  useEffect(() => {
    scheduleMetadataRef.current = scheduleMetadata;
  }, [scheduleMetadata]);

  // Väcker alla som väntar på att anslutningen ska bli klar (se waitForConnection nedan).
  // Anropas från anslutnings-effekten när connection.start()/onreconnected lyckas.
  const notifyConnectionReady = useCallback(() => {
    const resolvers = connectionReadyResolversRef.current;
    connectionReadyResolversRef.current = [];
    resolvers.forEach(resolve => resolve());
  }, []);

  // Väntar på att SignalR-anslutningen ska bli Connected — händelsedrivet (via
  // notifyConnectionReady) istället för att polla state var 100:e ms.
  const waitForConnection = useCallback((maxWaitTime = 10000) => {
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const onReady = () => {
        clearTimeout(timeoutId);
        resolve();
      };

      const timeoutId = setTimeout(() => {
        connectionReadyResolversRef.current =
          connectionReadyResolversRef.current.filter(r => r !== onReady);
        reject(new Error('SignalR anslutning tog för lång tid'));
      }, maxWaitTime);

      connectionReadyResolversRef.current.push(onReady);
    });
  }, []);

  // ========== SUB-HOOKS ==========
  const {
    editLocks,
    myLocks,
    setEditLocks,
    setMyLocks,
    requestEditLock,
    releaseLock,
    releaseAllLocks,
    cleanupLocks
  } = useEditLocks(connectionRef);

  const {
    typingUsers,
    setTypingUsers,
    startTyping,
    stopTyping,
    cleanupTyping
  } = useTypingIndicators(connectionRef);

  const {
    sendHTTPHeartBeat,
    startSignalRHeartbeat,
    stopSignalRHeartbeat
  } = useSignalRHeartbeat(connectionRef, setIsConnected);

  // ========== SCHEDULE FUNCTIONS ==========
  const refreshScheduleAsync = useCallback(async (date, userId) => {
    try {
      await waitForConnection(10000);

      const dateToUse = date || getTodaysDate();
      await connectionRef.current.invoke('RequestSchedule', station, dateToUse, userId);
      setError(null);
    } catch (err) {
      console.error('Error requesting schedule:', err);
      setError('Kunde inte hämta schemat');
    }
  }, [station, waitForConnection]);

  const refreshScheduleAllUsersAsync = useCallback(async (date, _station) => {
    try {
      if (_station === "") {
        _station = station;
      }

      await waitForConnection(10000);

      const dateToUse = date || getTodaysDate();
      await connectionRef.current.invoke('RequestScheduleAllUsers', _station, dateToUse, loggedInViaLink);
      setError(null);
    } catch (err) {
      console.error('Error requesting schedule (all users):', err);
      setError('Kunde inte hämta schemat');
    }
  }, [loggedInViaLink, waitForConnection]);

  const updateSchedule = useCallback(async (station, date, changes) => {
    try {
      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        await connectionRef.current.invoke('UpdateSchedule', station, date, changes);
      }
    } catch (err) {
      console.error('Error updating schedule:', err);
      setError('Kunde inte uppdatera schemat');
    }
  }, []);


  // ========== SAVE FUNCTIONS ==========
  const saveChanges = useCallback(async (station, date, entityId, changes, version) => {
    try {
      if (connectionRef.current?.state !== signalR.HubConnectionState.Connected) {
        throw new Error('Not connected');
      }

      const result = await connectionRef.current.invoke(
        'SaveScheduleChanges',
        station,
        date,
        entityId,
        changes,
        version
      );

      if (result.success) {
        setScheduleMetadata(prev => ({
          ...prev,
          version: result.version
        }));

        await releaseLock(station, date, entityId);

        return { success: true, version: result.version };
      } else {
        return { success: false, error: result.message };
      }
    } catch (err) {
      console.error('Error saving:', err);
      return { success: false, error: err.message };
    }
  }, [releaseLock]);

  // ========== DOCUMENT NOTIFICATION FUNCTIONS ==========
  const registerDocumentSavedCallback = useCallback((callback) => {
    documentSavedCallbackRef.current = callback;
  }, []);

  const notifyDocumentSaved = useCallback(async (station, date, userName) => {
    try {
      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        await connectionRef.current.invoke('NotifyDocumentSaved', {
          Station: station,
          Date: date,
          SavedBy: userName
        });
      }
    } catch (err) {
      console.error('Error notifying document save:', err);
    }
  }, []);

  // ========== WAKE UP HANDLING ==========
  useEffect(() => {
    const handleWakeUp = async () => {
      if (connectionRef.current?.state === signalR.HubConnectionState.Disconnected) {
        if (loggedInViaLink) {
          console.log('SignalR reconnectade – reloadar frontend för loggedInViaLink user');
          sessionStorage.clear();
          window.location.reload();
          return; // efter reload behövs inget annat
        }

        console.log('🔄 Forcing reconnect after wake');
        try {
          await sendHTTPHeartBeat();
          await connectionRef.current.start();
          if (!scheduleMetadata) {
            setScheduleMetadata({
              html: "---",
              isEdited: false,
              lastUpdated: getTodaysDate(),
              currentDate: getTodaysDate()
            });
          }
          console.log('✅ Reconnected successfully');
        } catch (err) {
          console.error('❌ Reconnect failed:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleWakeUp);
    window.addEventListener('focus', handleWakeUp);
    window.addEventListener('online', handleWakeUp);

    return () => {
      document.removeEventListener('visibilitychange', handleWakeUp);
      window.removeEventListener('focus', handleWakeUp);
      window.removeEventListener('online', handleWakeUp);
    };
  }, [sendHTTPHeartBeat]);


  // ========== SIGNALR CONNECTION (körs BARA en gång vid mount) ==========
  useEffect(() => {
    let cancelled = false;  // ✅ Abort-flag
    const startConnection = async (userId) => {
      try {
        const token = sessionStorage.getItem('jwt_token');

        if (!token) {
          console.error('No JWT token found');
          setError('Ingen autentisering - logga in igen');
          setIsConnected(false);
          return;
        }

        // ✅ Läs isPublicViewer från token
        let isPublicViewerFromToken = false;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          isPublicViewerFromToken = payload.IsPublicViewer === "true";
          isPublicViewerFromTokenRef.current = isPublicViewerFromToken;
          console.log('📋 IsPublicViewer from token:', isPublicViewerFromToken);
        } catch (err) {
          console.error('Failed to parse token:', err);
        }

        // Skapa anslutning
        const apiBaseUrl = window.location.origin;
        const hubUrl = createHubUrl(apiBaseUrl, userId);
        const connection = createConnection(hubUrl, token);

        // Skapa event handlers
        const handlers = createEventHandlers({
          setScheduleMetadata,
          setTypingUsers,
          setEditLocks,
          setMyLocks,
          refreshScheduleAsync,
          refreshScheduleAllUsersAsync,
          documentSavedCallbackRef,
          scheduleMetadataRef,
          stationRef,
          impersonatedUserId
        });

        // Registrera alla event handlers
        registerEventHandlers(connection, handlers);

        // Connection lifecycle events
        connection.onreconnecting((error) => {
          console.log('Attempting to reconnect...', error);
          setIsConnected(false);
          setError('Återansluter...');
          stopSignalRHeartbeat();
          isPublicViewerRegisteredRef.current = false;

          // ← LÄGG TILL DETTA
          if (loggedInViaLink) {
            const token = sessionStorage.getItem('jwt_token');
            if (!token) {
              console.log('Token saknas vid reconnect – reloadar');
              sessionStorage.clear();
              setTimeout(() => window.location.reload(), 2000);
              return;
            }
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              const expired = payload.exp * 1000 < Date.now();
              if (expired) {
                console.log('Token utgången vid reconnect – reloadar');
                sessionStorage.clear();
                setTimeout(() => window.location.reload(), 2000);
              }
            } catch (e) {
              sessionStorage.clear();
              window.location.reload();
            }
          }
        });

        connection.onreconnected(async () => {
          sendHTTPHeartBeat();
          console.log('Reconnected successfully');
          setIsConnected(true);
          setError(null);
          startSignalRHeartbeat();
          notifyConnectionReady();

          // ✅ Re-registrera PublicViewer efter reconnect — läs aktuell station via ref
          // (station kan ha hunnit ändras sedan denna callback registrerades).
          const stationAtReconnect = stationRef.current;
          if (isPublicViewerFromToken && loggedInViaLink && stationAtReconnect && stationAtReconnect !== '---') {
            try {
              await connection.invoke('RegisterPublicViewer', stationAtReconnect, true);
              isPublicViewerRegisteredRef.current = true;
              console.log('✅ PublicViewer re-registered after reconnect:', { station: stationAtReconnect });
            } catch (err) {
              console.error('❌ Failed to re-register PublicViewer:', err);
            }
          }

          // Begär data explicit efter reconnect
          try {
            const currentStation = stationAtReconnect || '---';
            if (isPublicViewerFromToken) {
              await connection.invoke('RequestScheduleAllUsers', currentStation, getTodaysDate(), loggedInViaLink);
            } else {
              await connection.invoke('RequestSchedule', currentStation, getTodaysDate(), userId);
            }
          } catch (err) {
            console.error('Failed to request schedule after reconnect:', err);
          }
        });

        connection.onclose((error) => {
          console.log('Connection closed', error);
          setIsConnected(false);
          isPublicViewerRegisteredRef.current = false;

          // Infoscreen / PublicLinkViewer — ladda om sidan så JWT och anslutning förnyas automatiskt
          if (loggedInViaLink) {
            console.log('SignalR stängdes – reloadar frontend för loggedInViaLink user');
            sessionStorage.clear();
            setTimeout(() => window.location.reload(), 3000); // kort fördröjning för att undvika reload-loop vid nätverksfel
            return;
          }

          if (error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
            setError('Session utgången - logga in igen');
          } else {
            setError('Anslutningen förlorades');
          }

          stopSignalRHeartbeat();
        });

        // Starta anslutning
        await connection.start();

        if (cancelled) {  // ✅ Hoppa ut om cleanup körts
          connection.stop();
          return;
        }

        setIsConnected(true);
        setError(null);
        connectionRef.current = connection;
        notifyConnectionReady();

        sendHTTPHeartBeat();
        startSignalRHeartbeat();

        console.log('✅ SignalR connection established');

        // ✅ Registrera PublicViewer DIREKT efter connection (körs EN GÅNG)
        const stationAtConnect = stationRef.current;
        if (isPublicViewerFromToken && loggedInViaLink && stationAtConnect && stationAtConnect !== '---') {
          try {
            console.log('🔍 Registering PublicViewer:', {
              isPublicViewer: isPublicViewerFromToken,
              loggedInViaLink,
              station: stationAtConnect
            });

            await connection.invoke('RegisterPublicViewer', stationAtConnect, true);
            isPublicViewerRegisteredRef.current = true;
            console.log('✅ PublicViewer registered for automatic updates:', { station: stationAtConnect });
          } catch (err) {
            console.error('❌ Failed to register PublicViewer:', err);
          }
        } else {
          console.log('ℹ️ Not a PublicViewer via link:', {
            isPublicViewer: isPublicViewerFromToken,
            loggedInViaLink,
            station: stationAtConnect
          });
        }

        // Begär initial data
        console.log('📤 Requesting initial schedule...');
        try {
          if (!cancelled) {  // ✅ Kontroll innan invoke
            const stationToUse = stationAtConnect || '---';
            await connection.invoke('RequestSchedule', stationToUse, getTodaysDate(), userId);
          }
        } catch (err) {
          console.error('❌ Failed to request initial schedule:', err);
        }
      } catch (err) {
        console.error('SignalR Connection Error:', err);

        if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
          setError('Autentisering misslyckades - logga in igen');
          sessionStorage.clear();
        } else {
          setError('Kunde inte ansluta till schemat');
          setIsConnected(false);

          // Försök igen om 5 sekunder
          setTimeout(() => {
            startConnection(impersonatedUserId);
          }, 5000);
        }
      }
    };

    startConnection(impersonatedUserId);

    return () => {
      cancelled = true;  // ✅ Sätt flag innan cleanup
      stopSignalRHeartbeat();
      cleanupTyping();
      cleanupLocks();
      isPublicViewerRegisteredRef.current = false;
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
    // OBS: "station" är medvetet INTE med här — se effekten nedan. Ett stationsbyte ska
    // begära nytt schema på den redan öppna anslutningen, inte riva ner och återansluta
    // hela SignalR-connection (vilket nollställer heartbeat, lås och typing-indikatorer).
  }, [impersonatedUserId, loggedInViaLink]);

  // ========== STATIONSBYTE PÅ BEFINTLIG ANSLUTNING ==========
  useEffect(() => {
    if (!hasHandledInitialStationRef.current) {
      // Första körningen sker vid mount — anslutnings-effekten ovan begär redan
      // initial data för stationen, så hoppa över här för att undvika ett dubbelanrop.
      hasHandledInitialStationRef.current = true;
      return;
    }

    const requestScheduleForNewStation = async () => {
      if (connectionRef.current?.state !== signalR.HubConnectionState.Connected) return;

      try {
        if (isPublicViewerFromTokenRef.current && loggedInViaLink && station && station !== '---') {
          await connectionRef.current.invoke('RegisterPublicViewer', station, true);
          isPublicViewerRegisteredRef.current = true;
          await connectionRef.current.invoke('RequestScheduleAllUsers', station, getTodaysDate(), loggedInViaLink);
        } else {
          await connectionRef.current.invoke('RequestSchedule', station || '---', getTodaysDate(), impersonatedUserId);
        }
        setError(null);
      } catch (err) {
        console.error('Failed to request schedule for new station:', err);
        setError('Kunde inte hämta schemat');
      }
    };

    requestScheduleForNewStation();
  }, [station]);


  // ========== RETURN PUBLIC API ==========
  return {
    // State
    scheduleMetadata,
    isConnected,
    error,
    typingUsers,
    editLocks,
    myLocks,

    // Schedule functions
    refreshScheduleAsync,
    refreshScheduleAllUsersAsync,
    updateSchedule,

    // Typing functions
    startTyping,
    stopTyping,

    // Lock functions
    requestEditLock,
    releaseLock,
    releaseAllLocks,

    // Save functions
    saveChanges,

    // Document notification functions
    registerDocumentSavedCallback,
    notifyDocumentSaved
  };
};
